import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Users, Save, Search, RefreshCw } from "lucide-react";

const API = "/api";
const UNASSIGNED = "__none__";

export default function DepartmentTemplates() {
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [edits, setEdits] = useState({}); // { dept_key: { full_template_id, short_template_id } }
  const [saving, setSaving] = useState({});

  const refresh = async () => {
    setLoading(true);
    try {
      const [d, t] = await Promise.all([
        axios.get(`${API}/assignments/departments`),
        axios.get(`${API}/templates/`),
      ]);
      setRows(d.data.departments || []);
      const tpls = t.data.templates || t.data || [];
      setTemplates(Array.isArray(tpls) ? tpls : []);
      setEdits({});
    } catch (e) {
      toast.error("Failed to load departments: " + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const fullTemplates = useMemo(() => templates.filter((t) => (t.mode || "").toUpperCase() === "FULL"), [templates]);
  const shortTemplates = useMemo(() => templates.filter((t) => (t.mode || "").toUpperCase() === "SHORT"), [templates]);

  const filteredRows = useMemo(() => {
    if (!filter) return rows;
    const f = filter.toLowerCase();
    return rows.filter((r) => r.department.toLowerCase().includes(f));
  }, [rows, filter]);

  const onPick = (deptKey, mode, value) => {
    const v = value === UNASSIGNED ? null : value;
    setEdits((prev) => ({
      ...prev,
      [deptKey]: {
        ...(prev[deptKey] || {}),
        [mode === "full" ? "full_template_id" : "short_template_id"]: v,
      },
    }));
  };

  const isDirty = (row) => {
    const e = edits[row.department_key];
    if (!e) return false;
    return (
      (e.full_template_id !== undefined && e.full_template_id !== row.full_template_id) ||
      (e.short_template_id !== undefined && e.short_template_id !== row.short_template_id)
    );
  };

  const saveRow = async (row) => {
    const e = edits[row.department_key] || {};
    const full = e.full_template_id !== undefined ? e.full_template_id : row.full_template_id || null;
    const short = e.short_template_id !== undefined ? e.short_template_id : row.short_template_id || null;
    setSaving((p) => ({ ...p, [row.department_key]: true }));
    try {
      await axios.put(`${API}/assignments/department`, {
        department: row.department,
        full_template_id: full,
        short_template_id: short,
      });
      toast.success(`Saved templates for ${row.department}`);
      await refresh();
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving((p) => ({ ...p, [row.department_key]: false }));
    }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="department-templates-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-[#171717]">Department Templates</h1>
          <p className="text-sm text-[#5E5E5E] mt-1">
            Assign different signature templates to different departments (e.g. Sales, Finance, CEO).
            Department-level assignments override brand defaults but are still overridden by user-level pinning.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="dept-refresh-btn">
          <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative max-w-md flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E5E5E]" />
          <Input
            placeholder="Filter departments…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pl-9 text-sm"
            data-testid="dept-filter"
          />
        </div>
        <span className="text-xs text-[#5E5E5E]">
          {rows.length} department{rows.length === 1 ? "" : "s"} from directory
        </span>
      </div>

      <div className="bg-white border border-[#E1DFDD] rounded-md overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#5E5E5E]">Loading departments…</div>
        ) : filteredRows.length === 0 ? (
          <div className="p-12 text-center" data-testid="dept-empty">
            <Building2 size={32} className="mx-auto text-[#5E5E5E] mb-3" strokeWidth={1.4} />
            <p className="text-sm text-[#5E5E5E]">
              {filter ? "No matching departments." : "No departments found in the directory yet."}
            </p>
            {!filter && (
              <p className="text-xs text-[#5E5E5E] mt-1">Sync your Microsoft Graph users first via Directory Sync.</p>
            )}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="dept-table">
            <thead className="bg-[#FAFAFA] text-[#5E5E5E] text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-medium w-[26%]">Department</th>
                <th className="text-left px-4 py-3 font-medium w-[10%]">Users</th>
                <th className="text-left px-4 py-3 font-medium">FULL template (new emails)</th>
                <th className="text-left px-4 py-3 font-medium">SHORT template (replies)</th>
                <th className="text-right px-4 py-3 font-medium w-[120px]">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => {
                const dirty = isDirty(row);
                const e = edits[row.department_key] || {};
                const fullVal = (e.full_template_id !== undefined ? e.full_template_id : row.full_template_id) || UNASSIGNED;
                const shortVal = (e.short_template_id !== undefined ? e.short_template_id : row.short_template_id) || UNASSIGNED;
                return (
                  <tr
                    key={row.department_key}
                    className={`border-t border-[#E1DFDD] ${dirty ? "bg-[#FFF8E5]" : "hover:bg-[#FAFAFA]"}`}
                    data-testid={`dept-row-${row.department_key}`}
                  >
                    <td className="px-4 py-3 text-[#171717] font-medium">{row.department}</td>
                    <td className="px-4 py-3 text-[#5E5E5E]">
                      <span className="inline-flex items-center gap-1">
                        <Users size={12} /> {row.user_count}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={fullVal} onValueChange={(v) => onPick(row.department_key, "full", v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`dept-full-${row.department_key}`}>
                          <SelectValue placeholder="Choose template…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={UNASSIGNED}>— Use brand default —</SelectItem>
                          {fullTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={shortVal} onValueChange={(v) => onPick(row.department_key, "short", v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`dept-short-${row.department_key}`}>
                          <SelectValue placeholder="Choose template…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={UNASSIGNED}>— Use brand default —</SelectItem>
                          {shortTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant={dirty ? "default" : "outline"}
                        disabled={!dirty || saving[row.department_key]}
                        onClick={() => saveRow(row)}
                        data-testid={`dept-save-${row.department_key}`}
                        className={dirty ? "bg-[#0F6CBD] hover:bg-[#0A57A0]" : ""}
                      >
                        <Save size={13} className="mr-1.5" />
                        {saving[row.department_key] ? "Saving…" : "Save"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-4 text-xs text-[#5E5E5E]">
        💡 After changing assignments, click{" "}
        <strong>Push to Relay</strong> on the Relay Control page so the VPS relay picks up the new mapping.
      </p>
    </div>
  );
}
